/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { getImageUrl } from '../utils/imageUrl';
import SEO from '../components/SEO';
import SectionRenderer from '../components/sections/SectionRenderer';
import { HOME_SECTION_REGISTRY } from '../components/sections/homeSectionRegistry';
import api from '../services/api';
import { formatPrice } from '../utils/format';
import { Button } from '../components/primitives';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import './CartPage.css';

const fmtAll = (value, currency = 'USD', lang) =>
  `${formatPrice(value, { lang })} ${currency}`;

const CartPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    cart,
    removeFromCart,
    updateQuantity,
    getCartSubtotal,
    getEffectiveShipping,
    calculateTax,
    calculateTotal,
    siteSettings,
  } = useCart();
  const { minOrderAmount, maxItemQuantity, currency: ctxCurrency, currencyLabel } = useSiteSettings();
  const currency = currencyLabel || ctxCurrency || t('common.currency');

  const [qtyEdits, setQtyEdits] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [stockWarnings, setStockWarnings] = useState({});
  const [cartLayout, setCartLayout] = useState(null);
  const stockFetchGen = useRef(0);
  const isEditorPreview = new URLSearchParams(window.location.search).get('themePreview') === '1';

  useEffect(() => {
    let active = true;
    api.get('/api/themes/templates/cart.default')
      .then((response) => {
        if (active) setCartLayout(response.data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isEditorPreview) return undefined;
    const receivePreview = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'STOREFRONT_LAYOUT_PREVIEW') return;
      setCartLayout(event.data.payload);
    };
    window.addEventListener('message', receivePreview);
    window.parent?.postMessage({ type: 'STOREFRONT_LAYOUT_PREVIEW_READY' }, window.location.origin);
    return () => window.removeEventListener('message', receivePreview);
  }, [isEditorPreview]);

  const refreshStockWarnings = useCallback(async () => {
    if (!cart.length) {
      setStockWarnings({});
      return;
    }
    const gen = ++stockFetchGen.current;
    const ids = [...new Set(cart.map((i) => i.id).filter((id) => id != null))];
    const entries = await Promise.all(
      ids.map(async (id) => {
        try {
          const { data } = await api.get(`/api/products/by-id/${id}`);
          return [id, data];
        } catch {
          return [id, null];
        }
      })
    );
    if (gen !== stockFetchGen.current) return;
    const byId = {};
    entries.forEach(([id, data]) => {
      if (data) byId[id] = data;
    });
    const next = {};
    cart.forEach((item) => {
      const key = item.cartKey || String(item.id);
      const p = byId[item.id];
      if (!p) return;
      const stock = Number(p.stock);
      const status = p.stockStatus;
      const qty = Number(item.quantity) || 0;
      if (status === 'out_of_stock' || !Number.isFinite(stock) || stock <= 0) {
        next[key] = { message: t('cart.outOfStock', 'Out of stock — please remove'), tone: 'error' };
      } else if (qty > stock) {
        next[key] = {
          message: t('cart.onlyNAvailable', 'Only {{count}} available', { count: stock }),
          tone: 'qty',
        };
      } else if (status === 'low_stock' || (Number.isFinite(stock) && stock > 0 && stock <= 5)) {
        next[key] = { message: t('cart.lowStock', 'Low stock'), tone: 'warn' };
      }
    });
    setStockWarnings(next);
  }, [cart, t]);

  useEffect(() => {
    if (!cart.length) {
      setStockWarnings({});
      return undefined;
    }
    const tmr = setTimeout(() => {
      refreshStockWarnings();
    }, 1000);
    return () => clearTimeout(tmr);
  }, [cart, refreshStockWarnings]);

  useEffect(() => {
    if (!cart.length) return undefined;
    const interval = setInterval(() => {
      refreshStockWarnings();
    }, 30000);
    return () => clearInterval(interval);
  }, [cart.length, refreshStockWarnings]);

  useEffect(() => {
    if (editingKey) return;
    const next = {};
    cart.forEach((item) => {
      const k = item.cartKey || String(item.id);
      next[k] = String(item.quantity);
    });
    setQtyEdits(next);
  }, [cart, editingKey]);

  const subtotal = getCartSubtotal();
  const shippingCost = getEffectiveShipping(subtotal);
  const tax = calculateTax(subtotal);
  const total = calculateTotal(subtotal, shippingCost);
  const taxLabel = siteSettings.taxIncludedInPrice
    ? t('common.taxIncluded', 'Tax (included)')
    : `${t('common.tax', 'Tax')} (${siteSettings.taxRate}%)`;

  const cartTotal = parseFloat(subtotal) || 0;
  const minOrder = parseFloat(minOrderAmount) || 0;
  const maxQ = parseInt(maxItemQuantity, 10) || 99;

  const threshold = parseFloat(siteSettings.freeShippingThreshold) || 0;
  const showFreeShipBar = threshold > 0 && cart.length > 0;
  const isFreeShip = showFreeShipBar && cartTotal >= threshold;
  const remaining = showFreeShipBar && !isFreeShip ? threshold - cartTotal : 0;
  const progress = showFreeShipBar ? Math.min((cartTotal / threshold) * 100, 100) : 0;

  const fmtMoney = (value) => fmtAll(value, currency, i18n.language);
  const cartSections = cartLayout?.template?.sections || [
    { key: 'cart-main', type: 'cart_main', enabled: true, position: 0, settings: {} },
  ];
  const cartMain = cartSections.find((section) => section.type === 'cart_main') || cartSections[0];
  const cartRecommendations = cartSections.find((section) => section.type === 'cart_recommendations' && section.enabled);

  const cartMainContent = (
      <div className="cart-page" data-section-key={cartMain.key}>
        <h1 className="cart-page__title">{t('cart.title', 'Your cart')}</h1>

        {cart.length === 0 ? (
          <div className="cart-empty">
            <Icon.Cart size={48} strokeWidth={1.25} aria-hidden="true" className="cart-empty__icon" />
            <p className="cart-empty__message">{t('cart.empty', 'Your cart is empty')}</p>
            <Button as="link" to="/products" variant="brand">
              {t('cart.shopNow', 'Shop now')}
            </Button>
          </div>
        ) : (
          <div className="cart-layout">
            <div className="cart-lines">
              {cart.map((item) => {
                const key = item.cartKey || String(item.id);
                const lineSub = parseFloat(item.price) * item.quantity;
                const warning = stockWarnings[key];
                return (
                  <div key={key} className="cart-line">
                    <div>
                      {item.image ? (
                        item.slug ? (
                          <button
                            type="button"
                            className="cart-line__thumb-link"
                            onClick={() => navigate(`/products/${item.slug}`)}
                            aria-label={t('cart.viewProduct', 'View product')}
                          >
                            <img
                              src={getImageUrl(item.image, { size: 'thumb' })}
                              alt=""
                              className="cart-line__thumb-img"
                              loading="lazy"
                              onError={(e) => {
                                if (!e.target.dataset.fallback) {
                                  e.target.dataset.fallback = '1';
                                  e.target.src = getImageUrl(item.image);
                                }
                              }}
                            />
                          </button>
                        ) : (
                          <div className="cart-line__thumb">
                            <img
                              src={getImageUrl(item.image, { size: 'thumb' })}
                              alt=""
                              className="cart-line__thumb-img"
                              loading="lazy"
                            />
                          </div>
                        )
                      ) : (
                        <div className="cart-line__thumb-placeholder" />
                      )}
                    </div>

                    <div className="cart-line__body">
                      {item.slug ? (
                        <Link to={`/products/${item.slug}`} className="cart-line__name">
                          {item.name}
                        </Link>
                      ) : (
                        <div className="cart-line__name">{item.name}</div>
                      )}

                      {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                        <div className="cart-line__options">
                          {Object.entries(item.selectedOptions).map(([k, v]) => (
                            <span key={`${key}-${k}`} className="cart-line__option-chip">
                              {k}: {v}
                            </span>
                          ))}
                        </div>
                      )}

                      {item.sku && (
                        <p className="cart-line__sku">SKU: {item.sku}</p>
                      )}
                      <p className="cart-line__unit-price">{fmtMoney(item.price)}</p>

                      <div className="cart-line__meta">
                        <div className="cart-qty">
                          <button
                            type="button"
                            className="cart-qty__btn"
                            onClick={() => updateQuantity(key, item.quantity - 1)}
                            aria-label={t('cart.decrease', 'Decrease quantity')}
                            disabled={item.quantity <= 1}
                          >
                            <Icon.Minus size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
                          </button>
                          <input
                            type="number"
                            min={1}
                            max={maxQ}
                            inputMode="numeric"
                            className="cart-qty__input"
                            value={qtyEdits[key] ?? String(item.quantity)}
                            onFocus={() => {
                              setEditingKey(key);
                              setQtyEdits((prev) => ({ ...prev, [key]: String(item.quantity) }));
                            }}
                            onChange={(e) => setQtyEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                            onBlur={() => {
                              setEditingKey(null);
                              const raw = qtyEdits[key] ?? String(item.quantity);
                              let n = parseInt(raw, 10);
                              if (Number.isNaN(n) || n < 1) {
                                updateQuantity(key, item.quantity);
                                setQtyEdits((prev) => ({ ...prev, [key]: String(item.quantity) }));
                                return;
                              }
                              n = Math.min(n, maxQ);
                              updateQuantity(key, n);
                              setQtyEdits((prev) => ({ ...prev, [key]: String(n) }));
                            }}
                          />
                          <button
                            type="button"
                            className="cart-qty__btn"
                            onClick={() => updateQuantity(key, Math.min(item.quantity + 1, maxQ))}
                            aria-label={t('cart.increase', 'Increase quantity')}
                            disabled={item.quantity >= maxQ}
                          >
                            <Icon.Plus size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      {warning && (
                        <p
                          role="alert"
                          className={`cart-line__warning cart-line__warning--${warning.tone}`}
                        >
                          {warning.message}
                        </p>
                      )}
                    </div>

                    <div className="cart-line__price">
                      <p className="cart-line__line-total">{fmtMoney(lineSub)}</p>
                      <button
                        type="button"
                        onClick={() => removeFromCart(key)}
                        title={t('common.remove', 'Remove')}
                        aria-label={t('common.remove', 'Remove')}
                        className="cart-line__remove"
                      >
                        <Icon.Trash size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <aside className="cart-summary">
              <h2 className="cart-summary__title">{t('cart.summary', 'Order summary')}</h2>

              {showFreeShipBar && (
                <div className="cart-progress">
                  <div className="cart-progress__bar">
                    <div
                      className={`cart-progress__fill ${isFreeShip ? 'cart-progress__fill--complete' : ''}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="cart-progress__label">
                    {isFreeShip
                      ? t('common.freeShippingQualified')
                      : t('common.freeShippingRemaining', {
                          amount: formatPrice(remaining, { lang: i18n.language }),
                          currency,
                        })}
                  </p>
                </div>
              )}

              <div className="cart-totals">
                <div className="cart-totals__row">
                  <span>{t('common.subtotal')}:</span>
                  <span className="cart-totals__row-value">{fmtMoney(subtotal)}</span>
                </div>
                <div className="cart-totals__row">
                  <span>{t('common.shipping')}:</span>
                  <span className="cart-totals__row-value">
                    {parseFloat(shippingCost) === 0
                      ? t('common.freeShipping', 'Free')
                      : fmtMoney(shippingCost)}
                  </span>
                </div>
                <div className="cart-totals__row">
                  <span>{taxLabel}:</span>
                  <span className="cart-totals__row-value">{fmtMoney(tax)}</span>
                </div>
                <div className="cart-totals__total">
                  <span>{t('common.total')}:</span>
                  <span className="cart-totals__row-value">{fmtMoney(total)}</span>
                </div>
              </div>

              {minOrder > 0 && cartTotal < minOrder && (
                <div className="cart-min-order-warning" role="alert">
                  {t('common.minOrderRemaining', {
                    amount: formatPrice(minOrder - cartTotal, { lang: i18n.language }),
                    currency,
                  })}
                </div>
              )}

              <div className="cart-actions">
                <Button
                  as="link"
                  to="/checkout"
                  variant="brand"
                  fullWidth
                  disabled={minOrder > 0 && cartTotal < minOrder}
                >
                  {t('cart.checkout', 'Proceed to checkout')}
                </Button>
                <Button as="link" to="/products" variant="secondary" fullWidth>
                  {t('cart.continueShopping', 'Continue shopping')}
                </Button>
              </div>
            </aside>
          </div>
        )}
      </div>
  );

  const cartRegions = [
    {
      key: cartMain.key,
      position: Number(cartMain.position) || 0,
      content: isEditorPreview ? (
        <div
          className="editor-preview-section"
          data-editor-preview-section={cartMain.key}
          onClickCapture={(event) => {
            event.preventDefault();
            event.stopPropagation();
            window.parent?.postMessage({ type: 'STOREFRONT_SECTION_SELECTED', sectionKey: cartMain.key }, window.location.origin);
          }}
        >
          {cartMainContent}
        </div>
      ) : cartMainContent,
    },
    ...(cartRecommendations ? [{
      key: cartRecommendations.key,
      position: Number(cartRecommendations.position) || 0,
      content: <SectionRenderer layout={{ template: { sections: [cartRecommendations] } }} registry={HOME_SECTION_REGISTRY} context={{ isEditorPreview }} />,
    }] : []),
  ].sort((left, right) => left.position - right.position);

  return (
    <>
      <SEO title={t('cart.title', 'Shopping Cart')} />
      {cartRegions.map((region) => <React.Fragment key={region.key}>{region.content}</React.Fragment>)}
    </>
  );
};

export default CartPage;
